import React from 'react';
import { 
  Box, 
  Typography, 
  Stack, 
  FormControlLabel, 
  Switch,
  Radio,
  RadioGroup,
  InputAdornment,
  Alert,
  Button,
  IconButton,
  Divider
} from '@mui/material';
import { 
  Policy as PolicyIcon,
  Pets as PetIcon,
  SmokingRooms as SmokingIcon,
  Group as GuestIcon,
  VolumeDown as QuietIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useFormContext, Controller, useFieldArray } from 'react-hook-form';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FormTextField from '../../shared/FormTextField';
import FormCheckboxGroup from '../../shared/FormCheckboxGroup';
import StepFormLayout from '../../shared/StepFormLayout';
import FormSection from '../../shared/FormSection';

interface PropertyPoliciesStepProps {
  stepProgress: number;
}

const PropertyPoliciesStep: React.FC<PropertyPoliciesStepProps> = ({ stepProgress }) => {
  const { control, watch } = useFormContext();
  const petsAllowed = watch('policies.pets.allowed');
  const quietHoursEnabled = watch('policies.quietHours.enabled');
  const petDetails = useFieldArray({
    control,
    name: 'policies.pets.details',
  });
  const progress = stepProgress;

  const petTypes = [
    { value: 'dog', label: 'Dogs' },
    { value: 'cat', label: 'Cats' },
    { value: 'bird', label: 'Birds' },
    { value: 'fish', label: 'Fish' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StepFormLayout
        title="Property Policies"
        description="Configure property policies including pets, smoking, and quiet hours"
        icon={<PolicyIcon sx={{ fontSize: 28, color: 'primary.main' }} />}
        progress={progress}
        showOptionalBadge={true}
      >
      <Box>
      
      {/* Quick tip */}
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          <strong>📋 Tip:</strong> Clear policies help prevent disputes and ensure both parties understand expectations.
        </Typography>
      </Alert>
      
      {/* Pet Policy */}
      <FormSection
        title="Pet Policy"
        icon={<PetIcon color="primary" />}
        tooltip="Pet allowances, restrictions, and associated fees"
        isRequired={false}
        defaultExpanded={false}
      >
        <Stack spacing={3}>
          <Controller
            name="policies.pets.allowed"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={field.value} />}
                label="Pets Allowed"
              />
            )}
          />
          
          {petsAllowed && (
            <>
              <FormCheckboxGroup
                name="policies.pets.types"
                control={control}
                label="Allowed Pet Types"
                options={petTypes}
              />
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <FormTextField
                  name="policies.pets.count"
                  control={control}
                  label="Maximum Number of Pets"
                  type="number"
                />
                <FormTextField
                  name="policies.pets.deposit"
                  control={control}
                  label="Pet Deposit"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
                <FormTextField
                  name="policies.pets.rent"
                  control={control}
                  label="Monthly Pet Rent"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Box>

              <Divider />

              <Stack spacing={2}>
                <Typography variant="subtitle2">Pet Details</Typography>
                {petDetails.fields.map((field, index) => (
                  <Box
                    key={field.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      position: 'relative',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                      }}
                    >
                      <IconButton
                        aria-label="Remove pet"
                        size="small"
                        onClick={() => petDetails.remove(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Stack spacing={2}>
                      <FormTextField
                        name={`policies.pets.details.${index}.name`}
                        control={control}
                        label="Pet Name / Identifier"
                        placeholder="e.g., Luna"
                      />
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                        <FormTextField
                          name={`policies.pets.details.${index}.type`}
                          control={control}
                          label="Type"
                          placeholder="Dog, Cat, etc."
                        />
                        <FormTextField
                          name={`policies.pets.details.${index}.breed`}
                          control={control}
                          label="Breed"
                          placeholder="e.g., Labrador"
                        />
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                        <FormTextField
                          name={`policies.pets.details.${index}.weight`}
                          control={control}
                          label="Weight"
                          type="number"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">lbs</InputAdornment>,
                          }}
                        />
                        <FormTextField
                          name={`policies.pets.details.${index}.age`}
                          control={control}
                          label="Age"
                          type="number"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">yrs</InputAdornment>,
                          }}
                        />
                      </Box>
                      <FormTextField
                        name={`policies.pets.details.${index}.description`}
                        control={control}
                        label="Notes / Description"
                        placeholder="Temperament, indoor/outdoor, special rules"
                        multiline
                        rows={2}
                      />
                    </Stack>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => petDetails.append({ name: '', type: '', breed: '', weight: undefined, age: undefined, description: '' })}
                  size="small"
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add Pet
                </Button>
                <FormTextField
                  name="policies.pets.notes"
                  control={control}
                  label="Pet Notes / Description"
                  placeholder="Freeform description of allowed pets (optional)"
                  multiline
                  rows={3}
                />
              </Stack>
            </>
          )}
        </Stack>
      </FormSection>
        
        {/* Smoking Policy */}
        <FormSection
          title="Smoking Policy"
          icon={<SmokingIcon color="primary" />}
          isRequired={false}
          defaultExpanded={false}
        >
          <Controller
            name="policies.smoking.policy"
            control={control}
            render={({ field }) => (
              <RadioGroup {...field}>
                <FormControlLabel 
                  value="not_permitted" 
                  control={<Radio />} 
                  label="Smoking Not Permitted" 
                />
                <FormControlLabel 
                  value="permitted" 
                  control={<Radio />} 
                  label="Smoking Permitted" 
                />
                <FormControlLabel 
                  value="outdoors_only" 
                  control={<Radio />} 
                  label="Smoking Permitted Outdoors Only" 
                />
              </RadioGroup>
            )}
          />
        </FormSection>
        
        {/* Guest Policy */}
        <FormSection
          title="Guest Policy"
          icon={<GuestIcon color="primary" />}
          tooltip="Overnight guest policies and restrictions"
          isRequired={false}
          defaultExpanded={false}
        >
          <Stack spacing={3}>
            <Controller
              name="policies.guests.allowed"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value} />}
                  label="Overnight Guests Allowed"
                />
              )}
            />
            
            {watch('policies.guests.allowed') && (
              <FormTextField
                name="policies.guests.maxNights"
                control={control}
                label="Maximum Nights per Month"
                type="number"
              />
            )}
          </Stack>
        </FormSection>
        
        {/* Quiet Hours */}
        <FormSection
          title="Quiet Hours"
          icon={<QuietIcon color="primary" />}
          tooltip="Noise restrictions and quiet hour enforcement"
          isRequired={false}
          defaultExpanded={false}
        >
          <Stack spacing={3}>
            <Controller
              name="policies.quietHours.enabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value} />}
                  label="Enforce Quiet Hours"
                />
              )}
            />
            
            {quietHoursEnabled && (
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Controller
                  name="policies.quietHours.startTime"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      label="Quiet Hours Start"
                      value={field.value ? new Date(`1970-01-01T${field.value}`) : null}
                      onChange={(newValue) => {
                        if (newValue) {
                          const hours = newValue.getHours().toString().padStart(2, '0');
                          const minutes = newValue.getMinutes().toString().padStart(2, '0');
                          field.onChange(`${hours}:${minutes}`);
                        } else {
                          field.onChange('');
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: {}
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  name="policies.quietHours.endTime"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      label="Quiet Hours End"
                      value={field.value ? new Date(`1970-01-01T${field.value}`) : null}
                      onChange={(newValue) => {
                        if (newValue) {
                          const hours = newValue.getHours().toString().padStart(2, '0');
                          const minutes = newValue.getMinutes().toString().padStart(2, '0');
                          field.onChange(`${hours}:${minutes}`);
                        } else {
                          field.onChange('');
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: {}
                        },
                      }}
                    />
                  )}
                />
              </Box>
            )}
          </Stack>
        </FormSection>
        

      </Box>
      </StepFormLayout>
    </LocalizationProvider>
  );
};

export default PropertyPoliciesStep; 
